import React, { useState, useEffect, useMemo, useCallback } from "react";

import {
  Box,
  Tooltip,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { useSnackbar } from "notistack";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { MRT_Localization_EN } from "material-react-table/locales/en";

import ImportModal from "./PersistenceImport.component";

import { get, post } from "../shared/http/httpService";

const Persistence = () => {
  const [importOpen, setImportOpen] = useState(false);
  const [data, setData] = useState([]);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const { t, i18n } = useTranslation("persistence");

  const client = useSelector((state) => state.client);

  const columns = useMemo(
    () => [
      {
        header: t("persistence:id"),
        accessorKey: "id",
        size: 300,
      },
      {
        header: t("persistence:updatedAt"),
        accessorKey: "updatedAt",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!value) return "";

          return new Date(value).toLocaleString("es-AR", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });
        },
      },
    ],
    [t],
  );

  const getMemories = useCallback(async () => {
    try {
      const response = await get("/persistence/memories");

      setData(response.data || []);
    } catch (error) {
      enqueueSnackbar(t("persistence:errorGettingMemories"), {
        variant: "error",
      });
    }
  }, []);

  const handleDeleteMemory = async () => {
    try {
      await post(`/persistence/memory/${selectedSessionId}`);

      setOpenDeleteModal(false);

      setSelectedSessionId(null);

      getMemories();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getMemories();
  }, [client.name, getMemories]);

  const handleExport = async () => {
    try {
      const response = await get("/report/persistence");
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "persistence.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar(t("persistence:errorExportCsvPersistence"), {
        variant: "error",
      });
    }
  };

  const handleViewMemory = async (id) => {
    try {
      const response = await get(`/persistence?id=${id}`);

      delete response.message;

      setSelectedMemory(response);

      setOpenViewModal(true);
    } catch (error) {
      enqueueSnackbar(t("persistence:errorGettingViewMemory"), {
        variant: "error",
      });
    }
  };

  return (
    <React.Fragment>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("persistence:persistence")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          mb: 3,
        }}>
        <Tooltip title={t("persistence:importTooltip")}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setImportOpen(true)}
            id="import">
            {t("persistence:import")}
          </Button>
        </Tooltip>

        <Tooltip title={t("persistence:exportTooltip")}>
          <Button
            variant="outlined"
            color="secondary"
            id="export"
            onClick={handleExport}>
            {t("persistence:export")}
          </Button>
        </Tooltip>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={data}
        enableRowActions
        enableGlobalFilter
        positionActionsColumn="last"
        localization={
          i18n.language === "ES" ? MRT_Localization_ES : MRT_Localization_EN
        }
        initialState={{
          sorting: [
            {
              id: "updatedAt",
              desc: true,
            },
          ],
        }}
        renderRowActions={({ row }) => (
          <Box>
            <Tooltip title={t("persistence:viewmemory")}>
              <IconButton
                id="view-memory"
                onClick={() => handleViewMemory(row.original.id)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("persistence:deletememory")}>
              <IconButton
                color="error"
                id="delete-memory"
                onClick={() => {
                  setSelectedSessionId(row.original.id);
                  setOpenDeleteModal(true);
                }}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />

      <Dialog
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{t("persistence:sessiondetail")}</DialogTitle>

        <DialogContent>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}>
            {JSON.stringify(selectedMemory, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <DialogTitle>{t("persistence:confirmdelete")}</DialogTitle>

        <DialogContent>
          {t("persistence:modaldeletetext")}
          <strong> {selectedSessionId}</strong>?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>
            {t("persistence:modalcancelbutton")}
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteMemory}>
            {t("persistence:modalacceptbutton")}
          </Button>
        </DialogActions>
      </Dialog>

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} />
    </React.Fragment>
  );
};

export default Persistence;
